import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader, EmptyState, FingerprintChip } from "../components/UI";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import {
  Workflow as WorkflowIcon, Plus, Trash2, X, Save, GripVertical, ArrowUp, ArrowDown,
  Copy, LayoutTemplate, Search, Clock, Users as UsersIcon, Settings2, ToggleLeft, ToggleRight,
} from "lucide-react";
const ROLE_OPTIONS = [
  { value: "employee", label: "Employee" },
  { value: "manager", label: "Manager" },
  { value: "hr", label: "HR" },
  { value: "company_admin", label: "Company Admin" },
  { value: "super_admin", label: "Super Admin" },
];

const CATEGORIES = ["case", "policy", "evidence", "offboarding", "onboarding", "custom"];

const emptyStep = (order = 0) => ({
  id: crypto.randomUUID(),
  name: "",
  description: "",
  assignee_role: "hr",
  required_approvals: 1,
  sla_hours: 48,
  actions: [],
  order,
});

export default function Workflows() {
  const { user } = useAuth();
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // workflow object or null
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("mine"); // mine | templates
  const canManage = ["company_admin", "manager", "super_admin"].includes(user.role);

  const load = async () => {
    try {
      const [{ data: mine }, { data: tpl }] = await Promise.all([
        api.get("/workflows", { params: { is_template: false } }),
        api.get("/workflows", { params: { is_template: true } }),
      ]);
      setWorkflows(mine);
      setTemplates(tpl);
    } catch { toast.error("Failed to load workflows"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filteredMine = useMemo(() => workflows.filter(w =>
    !q || w.name.toLowerCase().includes(q.toLowerCase()) || (w.description || "").toLowerCase().includes(q.toLowerCase())
  ), [workflows, q]);
  const filteredTpl = useMemo(() => templates.filter(w =>
    !q || w.name.toLowerCase().includes(q.toLowerCase()) || (w.description || "").toLowerCase().includes(q.toLowerCase())
  ), [templates, q]);

  const openNew = () => setEditing({
    id: null,
    name: "",
    description: "",
    category: "case",
    trigger: "",
    status: "draft",
    steps: [emptyStep(0)],
  });

  const openEdit = (w) => setEditing({
    ...w,
    trigger: w.trigger || "",
    steps: (w.steps || []).map((s, i) => ({ ...s, id: s.id || crypto.randomUUID(), order: i })),
  });

  const cloneTemplate = async (t) => {
    try {
      const { data } = await api.post(`/workflows/${t.id}/clone-template`);
      toast.success(`Cloned "${t.name}"`);
      await load();
      openEdit(data);
      setTab("mine");
    } catch { toast.error("Failed to clone template"); }
  };

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Workflow name required");
    if (editing.steps.length === 0) return toast.error("Add at least one step");
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        description: editing.description || null,
        category: editing.category,
        trigger: editing.trigger || null,
        steps: editing.steps.map((s, i) => ({
          id: s.id,
          name: s.name,
          description: s.description || null,
          assignee_role: s.assignee_role,
          required_approvals: Number(s.required_approvals) || 1,
          sla_hours: Number(s.sla_hours) || 48,
          actions: s.actions || [],
          order: i,
        })),
      };
      if (editing.id) {
        await api.patch(`/workflows/${editing.id}`, payload);
      } else {
        await api.post("/workflows", payload);
      }
      toast.success("Workflow saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const del = async (w) => {
    if (!window.confirm(`Archive workflow "${w.name}"?`)) return;
    try {
      await api.delete(`/workflows/${w.id}`);
      toast.success("Archived");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <SectionHeader
        title="Workflow Designer"
        subtitle="Design multi-step approval and investigation workflows. Clone a system template or build your own , every version is fingerprinted."
        right={canManage ? (
          <button className="btn-primary" onClick={openNew} data-testid={TID.workflowCreateBtn}>
            <Plus size={14} strokeWidth={2}/> New workflow
          </button>
        ) : null}
      />

      {canManage && user.tenant_id && <ConfigPanel tenantId={user.tenant_id} />}

      <div className="data-cell p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2}/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search workflows…" className="input pl-9" data-testid="workflow-search"/>
        </div>
        <div className="border border-slate-200 flex text-xs font-mono">
          <button onClick={() => setTab("mine")} className={"px-3 py-1.5 " + (tab === "mine" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")} data-testid="workflow-tab-mine">
            My workflows ({filteredMine.length})
          </button>
          <button onClick={() => setTab("templates")} className={"px-3 py-1.5 " + (tab === "templates" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")} data-testid="workflow-tab-templates">
            <LayoutTemplate size={12} strokeWidth={2} className="inline mr-1"/> Templates ({filteredTpl.length})
          </button>
        </div>
      </div>

      {tab === "mine" && (
        filteredMine.length === 0 ? (
          <EmptyState
            icon={WorkflowIcon}
            title="No custom workflows yet"
            subtitle="Start from a system template or design your own from scratch."
            action={canManage ? <button className="btn-primary" onClick={openNew}><Plus size={14}/> New workflow</button> : null}
          />
        ) : (
          <div className="grid gap-3">
            {filteredMine.map((w) => (
              <WorkflowRow key={w.id} w={w} onEdit={openEdit} onDelete={del} canManage={canManage}/>
            ))}
          </div>
        )
      )}

      {tab === "templates" && (
        <div className="grid gap-3">
          {filteredTpl.map((w) => (
            <TemplateRow key={w.id} w={w} onClone={cloneTemplate} canManage={canManage}/>
          ))}
        </div>
      )}

      <AnimatePresence>
        {editing && (
          <EditorModal
            editing={editing}
            setEditing={setEditing}
            onSave={save}
            saving={saving}
            onClose={() => setEditing(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ConfigPanel({ tenantId }) {
  const [tenant, setTenant] = useState(null);
  const [saving, setSaving] = useState(false);
  const [posterVariant, setPosterVariant] = useState("classic");
  const [posterBusy, setPosterBusy] = useState(false);
  const [retention, setRetention] = useState(null);
  const [retentionBusy, setRetentionBusy] = useState(false);
  useEffect(() => {
    api.get(`/tenants/${tenantId}`).then((r) => setTenant(r.data)).catch(() => {});
    api.get(`/admin/retention/preview`).then((r) => setRetention(r.data.per_tenant?.[0])).catch(() => {});
  }, [tenantId]);
  const toggle = async (field) => {
    setSaving(true);
    try {
      const next = { [field]: !tenant[field] };
      const { data } = await api.patch(`/tenants/${tenantId}/workflow-config`, next);
      setTenant(data);
      toast.success(`${field.replace(/_/g, " ")} updated`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };
  const changeFreq = async (v) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/tenants/${tenantId}/workflow-config`, { hris_sync_frequency: v });
      setTenant(data);
      toast.success("HRIS sync frequency updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };
  const downloadPoster = async () => {
    setPosterBusy(true);
    try {
      const resp = await api.get(`/tenants/${tenantId}/intake-poster.pdf`, {
        params: { variant: posterVariant }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `prophr-intake-poster-${posterVariant}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Intake poster downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Poster generation failed"); }
    finally { setPosterBusy(false); }
  };
  const runPurge = async () => {
    if (!confirm("Anonymize all expired closed cases now? This cannot be undone.")) return;
    setRetentionBusy(true);
    try {
      const { data } = await api.post("/admin/retention/purge-now?mode=anonymize");
      toast.success(`Purged ${data.total_processed} case(s)`);
      const { data: p } = await api.get("/admin/retention/preview");
      setRetention(p.per_tenant?.[0]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Purge failed"); }
    finally { setRetentionBusy(false); }
  };
  if (!tenant) return null;
  return (
    <div className="mb-6 data-cell p-5" data-testid="workflow-config-panel">
      <div className="flex items-center gap-2 mb-4">
        <Settings2 size={14} className="text-indigo-700" strokeWidth={2}/>
        <div className="eyebrow">Admin Configuration Engine</div>
      </div>
      <div className="text-xs text-slate-500 mb-4">
        Control the PropHR default 7-step case pipeline. Toggles below are enforced across all
        workflows that reference the optional steps.
      </div>
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <ConfigToggle
          label="Compliance Review step"
          hint="Optional. If off, cases skip the compliance review gate."
          value={tenant.compliance_review_enabled}
          onToggle={() => toggle("compliance_review_enabled")}
          disabled={saving}
          testid="config-toggle-compliance"
        />
        <ConfigToggle
          label="Final Review step"
          hint="Optional. If off, findings are approved without a company-admin final review."
          value={tenant.final_review_enabled}
          onToggle={() => toggle("final_review_enabled")}
          disabled={saving}
          testid="config-toggle-final"
        />
        <div>
          <div className="text-sm font-medium text-slate-900 mb-1">HRIS Sync frequency</div>
          <div className="text-[11px] text-slate-500 mb-2">
            How often the involved-parties directory refreshes from the connected HRIS.
          </div>
          <select
            className="input !py-1.5"
            value={tenant.hris_sync_frequency || "manual"}
            onChange={(e) => changeFreq(e.target.value)}
            disabled={saving}
            data-testid="config-hris-frequency"
          >
            <option value="manual">Manual</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 border-t border-slate-200 pt-5">
        <div data-testid="intake-poster-block">
          <div className="text-sm font-medium text-slate-900 mb-1">Intake QR poster</div>
          <div className="text-[11px] text-slate-500 mb-2">
            One-click branded PDF for elevators, break rooms, and safety notices. QR points to your public intake page.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="input !py-1.5 !w-auto"
              value={posterVariant}
              onChange={(e) => setPosterVariant(e.target.value)}
              data-testid="poster-variant"
            >
              <option value="classic">Classic (light)</option>
              <option value="dark">Dark</option>
              <option value="serene">Serene (violet)</option>
            </select>
            <button
              className="btn-primary text-xs"
              onClick={downloadPoster}
              disabled={posterBusy}
              data-testid="poster-download"
            >
              {posterBusy ? "Generating..." : "Download poster PDF"}
            </button>
          </div>
        </div>
        <div data-testid="retention-block">
          <div className="text-sm font-medium text-slate-900 mb-1">Retention automation</div>
          <div className="text-[11px] text-slate-500 mb-2">
            Closed cases older than your retention window are anonymized every night. Preview or run now.
          </div>
          <div className="text-xs text-slate-700 mb-2">
            {retention ? (
              <>
                Retention window: <span className="font-mono">{retention.retention_days}</span> days.
                Cases eligible now: <span className="font-mono">{retention.expired_case_count}</span>.
              </>
            ) : "Loading..."}
          </div>
          <button
            className="btn-ghost text-xs"
            onClick={runPurge}
            disabled={retentionBusy}
            data-testid="retention-purge-now"
          >
            {retentionBusy ? "Purging..." : "Anonymize expired cases now"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfigToggle({ label, hint, value, onToggle, disabled, testid }) {
  return (
    <div>
      <div className="text-sm font-medium text-slate-900 mb-1">{label}</div>
      <div className="text-[11px] text-slate-500 mb-2">{hint}</div>
      <button
        onClick={onToggle}
        disabled={disabled}
        className={"inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded border " +
          (value
            ? "bg-emerald-50 border-emerald-300 text-emerald-800"
            : "bg-slate-50 border-slate-300 text-slate-700")}
        data-testid={testid}
      >
        {value ? <ToggleRight size={16} strokeWidth={2}/> : <ToggleLeft size={16} strokeWidth={2}/>}
        {value ? "Enabled" : "Disabled"}
      </button>
    </div>
  );
}



function WorkflowRow({ w, onEdit, onDelete, canManage }) {
  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="data-cell p-5" data-testid={`workflow-row-${w.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={"pill " + (w.status === "active" ? "pill-approved" : w.status === "archived" ? "pill-archived" : "pill-draft")}>{w.status}</span>
            <span className="text-[10px] font-mono text-slate-500">{w.category}</span>
            <span className="text-[10px] font-mono text-slate-500">{w.steps.length} steps</span>
          </div>
          <div className="font-display text-lg font-semibold text-slate-900">{w.name}</div>
          {w.description && <div className="text-sm text-slate-600 mt-0.5">{w.description}</div>}
          <div className="mt-2"><FingerprintChip fingerprint={w.fingerprint}/></div>
        </div>
        {canManage && (
          <div className="flex items-center gap-1">
            <button className="btn-ghost text-xs" onClick={() => onEdit(w)}>Edit</button>
            <button className="btn-ghost text-xs !text-red-600" onClick={() => onDelete(w)}><Trash2 size={12}/></button>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {w.steps.map((s, i) => (
          <div key={s.id} className="flex items-center gap-1.5">
            <div className="border border-slate-300 bg-slate-50 px-2 py-1 text-[11px]">
              <span className="text-slate-400 font-mono mr-1">{i + 1}.</span>
              <span className="text-slate-800">{s.name}</span>
              <span className="text-slate-400 font-mono ml-1">· {s.assignee_role}</span>
            </div>
            {i < w.steps.length - 1 && <span className="text-slate-300">→</span>}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function TemplateRow({ w, onClone, canManage }) {
  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="data-cell p-5" data-testid={`template-row-${w.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="pill pill-approved"><LayoutTemplate size={10} strokeWidth={2}/> template</span>
            <span className="text-[10px] font-mono text-slate-500">{w.category}</span>
            <span className="text-[10px] font-mono text-slate-500">{w.steps.length} steps</span>
          </div>
          <div className="font-display text-lg font-semibold text-slate-900">{w.name}</div>
          {w.description && <div className="text-sm text-slate-600 mt-0.5">{w.description}</div>}
        </div>
        {canManage && (
          <button className="btn-primary" onClick={() => onClone(w)} data-testid={TID.workflowClone(w.id)}>
            <Copy size={14} strokeWidth={2}/> Use template
          </button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {w.steps.map((s, i) => (
          <div key={s.id || i} className="flex items-center gap-1.5">
            <div className="border border-slate-300 bg-white px-2 py-1 text-[11px]">
              <span className="text-slate-400 font-mono mr-1">{i + 1}.</span>
              <span className="text-slate-800">{s.name}</span>
              <span className="text-slate-400 font-mono ml-1">· {s.assignee_role} · {s.sla_hours}h</span>
            </div>
            {i < w.steps.length - 1 && <span className="text-slate-300">→</span>}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function EditorModal({ editing, setEditing, onSave, saving, onClose }) {
  const setField = (k, v) => setEditing({ ...editing, [k]: v });
  const setStep = (idx, patch) => {
    const steps = editing.steps.map((s, i) => i === idx ? { ...s, ...patch } : s);
    setEditing({ ...editing, steps });
  };
  const addStep = () => setEditing({ ...editing, steps: [...editing.steps, emptyStep(editing.steps.length)] });
  const removeStep = (idx) => setEditing({ ...editing, steps: editing.steps.filter((_, i) => i !== idx) });
  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= editing.steps.length) return;
    const copy = [...editing.steps];
    const [item] = copy.splice(idx, 1);
    copy.splice(to, 0, item);
    setEditing({ ...editing, steps: copy });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 12, opacity: 0 }}
        className="bg-white border border-slate-300 w-full max-w-4xl max-h-[92vh] overflow-y-auto"
      >
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-display text-xl font-semibold">{editing.id ? "Edit workflow" : "New workflow"}</h3>
            <div className="text-xs text-slate-500">Design the sequence, assignees, and SLA of each step.</div>
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="eyebrow block mb-1">Name</label>
              <input value={editing.name} onChange={(e) => setField("name", e.target.value)} className="input" data-testid={TID.workflowName}/>
            </div>
            <div>
              <label className="eyebrow block mb-1">Category</label>
              <select value={editing.category} onChange={(e) => setField("category", e.target.value)} className="input">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-1">Description</label>
            <textarea value={editing.description || ""} onChange={(e) => setField("description", e.target.value)} rows={2} className="input"/>
          </div>

          <div>
            <label className="eyebrow block mb-1">Trigger (optional)</label>
            <input
              value={editing.trigger || ""}
              onChange={(e) => setField("trigger", e.target.value)}
              placeholder="e.g. case_category:Harassment,Discrimination"
              className="input"
            />
            <div className="text-[11px] text-slate-500 mt-1">Free-form key. Used by future automation to select which workflow to run.</div>
          </div>

          <div className="pt-2 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow">Steps ({editing.steps.length})</div>
              <button className="btn-ghost text-xs" onClick={addStep} data-testid={TID.workflowAddStep}>
                <Plus size={12}/> Add step
              </button>
            </div>

            <div className="space-y-3">
              {editing.steps.map((s, idx) => (
                <motion.div
                  key={s.id}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-slate-300 p-3 bg-slate-50"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <GripVertical size={14} className="text-slate-400"/>
                    <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-[11px] font-semibold shrink-0">{idx + 1}</div>
                    <input
                      value={s.name}
                      onChange={(e) => setStep(idx, { name: e.target.value })}
                      placeholder="Step name (e.g. 'HR triage')"
                      className="input !py-1.5 flex-1"
                      data-testid={`workflow-step-name-${idx}`}
                    />
                    <div className="flex items-center gap-1">
                      <button className="btn-ghost text-xs" onClick={() => move(idx, -1)} disabled={idx === 0}><ArrowUp size={12}/></button>
                      <button className="btn-ghost text-xs" onClick={() => move(idx, 1)} disabled={idx === editing.steps.length - 1}><ArrowDown size={12}/></button>
                      <button className="btn-ghost text-xs !text-red-600" onClick={() => removeStep(idx)}><Trash2 size={12}/></button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-0.5 flex items-center gap-1">
                        <UsersIcon size={10}/> Assignee role
                      </label>
                      <select value={s.assignee_role} onChange={(e) => setStep(idx, { assignee_role: e.target.value })} className="input !py-1.5">
                        {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-0.5 flex items-center gap-1">
                        <Clock size={10}/> SLA (hours)
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={s.sla_hours}
                        onChange={(e) => setStep(idx, { sla_hours: e.target.value })}
                        className="input !py-1.5"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-0.5">Required approvals</label>
                      <input
                        type="number"
                        min={1}
                        value={s.required_approvals}
                        onChange={(e) => setStep(idx, { required_approvals: e.target.value })}
                        className="input !py-1.5"
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-0.5 block">Description</label>
                    <textarea
                      value={s.description || ""}
                      onChange={(e) => setStep(idx, { description: e.target.value })}
                      rows={2}
                      className="input !py-1.5"
                      placeholder="What happens in this step?"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={onSave} disabled={saving} data-testid={TID.workflowSave}>
            <Save size={14} strokeWidth={2}/> {saving ? "Saving…" : "Save workflow"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
